import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle, Download } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { locationPages, serviceDetails } from '../mockData';

const SITE_URL = 'https://www.hexalogixautomation.com';

export const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const service = serviceDetails[serviceSlug];
  const pageUrl = `${SITE_URL}/services/${serviceSlug}`;
  const regionalPages = Object.values(locationPages).filter((page) => page.serviceSlug === serviceSlug);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: service.title,
      description: service.seoDescription,
      provider: {
        '@type': 'LocalBusiness',
        name: 'Hexalogix Automation',
        url: SITE_URL,
        telephone: '+971-55-916-2533',
        areaServed: ['AE', 'IN', 'Middle East', 'Africa']
      },
      serviceType: service.title,
      areaServed: ['UAE', 'Middle East', 'Africa', 'India'],
      url: pageUrl
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Services',
          item: `${SITE_URL}/services`
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: service.title,
          item: pageUrl
        }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: service.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  ];

  return (
    <>
      <SEOHead
        title={service.seoTitle}
        description={service.seoDescription}
        keywords={service.keywords}
        canonical={pageUrl}
        schemas={schemas}
      />

      <div className="min-h-screen">
        <section className="relative pt-16 md:pt-[106px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={service.heroImage}
              alt={`${service.title} for UAE and Africa projects`}
              width="1600"
              height="900"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E]/90 via-[#0A0F1E]/75 to-[#0A0F1E]/40" />
          </div>
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8]" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-300">{service.title}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-6">
              {service.title}
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl leading-relaxed mb-8">
              {service.heroDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <button className="inline-flex items-center gap-2 bg-[#0057B8] hover:bg-[#003D82] text-white font-bold px-8 py-4 text-base tracking-wide transition-all duration-200 hover:shadow-xl hover:shadow-[#0057B8]/40 group">
                  Request a Quote
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <a
                href="/Hexalogix-Brochure.pdf"
                download="Hexalogix-Automation-Brochure.pdf"
                className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold px-8 py-4 text-base tracking-wide transition-all duration-200 hover:bg-white/10 group"
              >
                <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                Download Brochure
              </a>
            </div>
          </div>
        </section>

        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">
                    {service.introTitle}
                  </span>
                </div>
                {service.introBody.map((paragraph, index) => (
                  <p key={index} className="text-gray-600 leading-relaxed mb-6 text-lg">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="bg-[#F0F4F9] border border-gray-200 p-8">
                <h2 className="text-2xl sm:text-3xl font-black text-[#0A0F1E] mb-6">
                  What You Get
                </h2>
                <div className="space-y-4">
                  {service.highlightPoints.map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#0057B8] mt-0.5 flex-shrink-0" />
                      <span className="text-base text-gray-700 leading-relaxed">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {regionalPages.length > 0 && (
              <div className="mt-14 border-t border-gray-200 pt-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-[2px] bg-[#0057B8]" />
                  <span className="text-[#0057B8] text-sm font-semibold tracking-widest uppercase">Regional Focus</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {regionalPages.map((page) => (
                    <Link
                      key={page.slug}
                      to={`/locations/${page.slug}`}
                      className="inline-flex items-center gap-2 border border-gray-300 hover:border-[#0057B8] text-[#0A0F1E] hover:text-[#0057B8] px-4 py-3 text-sm font-semibold transition-colors"
                    >
                      {page.title}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-4">
                {service.expertiseTitle}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {service.expertiseItems.map((item) => (
                <div key={item.title} className="bg-white p-8 shadow-lg hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-[#0A0F1E] mb-4">{item.title}</h3>
                  <p className="text-gray-600 mb-6 text-base leading-relaxed">{item.description}</p>
                  <ul className="space-y-2">
                    {item.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-center gap-2 text-base text-gray-700">
                        <CheckCircle className="w-4 h-4 text-[#0057B8] flex-shrink-0" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-4">
                {service.industriesTitle}
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {service.industries.map((industry) => (
                <div key={industry} className="text-center p-6 border border-gray-200 hover:border-[#0057B8] transition-colors bg-white">
                  <h3 className="font-bold text-[#0A0F1E] text-base">{industry}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-4">
                {service.processTitle}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {service.process.map((step) => (
                <div key={step.step} className="text-center">
                  <div className="w-16 h-16 bg-[#0057B8] text-white font-black text-xl flex items-center justify-center mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-bold text-[#0A0F1E] mb-2 text-base">{step.title}</h3>
                  <p className="text-base text-gray-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {service.projectImages && (
          <section className="bg-white py-20 md:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto text-center mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#0057B8]" />
                  <span className="text-[#0057B8] text-sm font-semibold tracking-widest uppercase">
                    Our Work
                  </span>
                  <span className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#0057B8]" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-4">
                  Recent Panel Projects
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Real examples of control panel manufacturing, PLC panel wiring, HMI panels, and low-voltage switchgear-related work delivered for industrial automation projects.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.projectImages.map((image, index) => (
                  <figure
                    key={image.src}
                    className={`${index === 0 ? 'lg:col-span-2' : ''} group bg-[#F0F4F9] border border-gray-200 overflow-hidden`}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-white">
                      <img
                        src={image.src}
                        alt={image.alt}
                        width="900"
                        height="700"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <figcaption className="p-4">
                      <h3 className="text-base font-bold text-[#0A0F1E]">{image.title}</h3>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">
                    Case Study
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-6">
                  {service.caseStudy.title}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                  {service.caseStudy.description}
                </p>
                <Link to="/contact">
                  <button className="inline-flex items-center gap-2 bg-[#0057B8] hover:bg-[#003D82] text-white font-semibold px-6 py-3 text-base tracking-wide transition-all duration-200 group">
                    Discuss Your Project
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>

              <div className="bg-[#0A0F1E] text-white p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-6">
                  {service.caseStudy.metrics.map((metric) => (
                    <div key={metric.label} className="border-b border-white/10 pb-4">
                      <div className="text-2xl font-black text-[#60A5FA] mb-1">{metric.value}</div>
                      <div className="text-sm text-gray-400 uppercase tracking-widest">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-6">
              {service.faqs.map((faq) => (
                <div key={faq.question} className="border border-gray-200 bg-white p-6 hover:border-[#0057B8]/30 transition-colors">
                  <h3 className="font-bold text-[#0A0F1E] text-lg mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed text-base">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative bg-[#0057B8] py-20 md:py-24 overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-[#003D82]/40 -skew-x-12 translate-x-16" />
          <div className="absolute right-16 top-0 bottom-0 w-16 bg-white/5 -skew-x-12" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-4">
              {service.ctaTitle}
            </h2>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto mb-8">
              {service.ctaText}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <button className="inline-flex items-center gap-2 bg-white text-[#0057B8] hover:bg-gray-100 font-bold px-8 py-4 text-base tracking-wide transition-all duration-200 hover:shadow-xl group">
                  Contact Hexalogix
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/services">
                <button className="inline-flex items-center gap-2 border-2 border-white/50 hover:border-white text-white font-semibold px-8 py-4 text-base tracking-wide transition-all duration-200 hover:bg-white/10">
                  View All Services
                </button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
